import { cn } from "@/lib/utils";

/**
 * LoadingSpinner - Eine anpassbare Ladeanimation-Komponente
 *
 * Diese Komponente zeigt eine rotierende Kreis-Animation mit optionalem Text an.
 * Sie ist vollständig anpassbar in Größe, Border-Breite und Text.
 *
 * @example
 * ```tsx
 * // Standard-Spinner
 * <LoadingSpinner />
 *
 * // Kleiner Spinner für Bild-Loading
 * <LoadingSpinner size={24} width={2} text="Lädt..." />
 *
 * // Großer, prominenter Spinner
 * <LoadingSpinner size={64} width={6} text="Daten werden geladen..." />
 * ```
 */
export default function LoadingSpinner({
  text,
  className = "",
  textClassName = "",
  size = 32,
  width = 4,
}: {
  /**
   * Der Text, der neben dem Spinner angezeigt wird
   * @default ""
   * @example "Daten werden geladen...", "Bild wird hochgeladen..."
   */
  text?: string;

  /**
   * Zusätzliche CSS-Klassen für das Container-Element
   * @default ""
   * @example "my-4", "text-center", "bg-gray-50"
   */
  className?: string;

  /**
   * Die Größe des Spinners in Pixeln (Breite und Höhe)
   * @default 32
   * @example 16 (sehr klein), 24 (klein), 48 (mittel), 64 (groß)
   */
  size?: number;

  /**
   * Zusätzliche CSS-Klassen für den Text
   * @default ""
   * @example "text-white", "text-gray-500"
   */
  textClassName?: string;
  /**
   * Die Breite des Spinner-Borders in Pixeln
   * @default 4
   * @example 1 (sehr dünn), 2 (dünn), 6 (dick), 8 (sehr dick)
   */
  width?: number;
}) {
  return (
    <div className={cn("flex items-center justify-center py-12", className)}>
      {/* Spinner Container */}
      <div className="relative">
        {/* Hintergrund-Kreis (statisch) */}
        <div
          className="rounded-full border-gray-200"
          style={{
            width: size,
            height: size,
            borderWidth: width,
          }}
        ></div>
        {/* Rotierender Kreis (Animation) */}
        <div
          className="border-primary absolute top-0 left-0 animate-spin rounded-full border-t-transparent"
          style={{
            width: size,
            height: size,
            borderWidth: width,
          }}
        ></div>
      </div>
      {/* Optionaler Text */}
      {text && (
        <span className={cn("ml-3 text-gray-600", textClassName)}>{text}</span>
      )}
    </div>
  );
}
